import {
  BILL_STATUS,
  BILL_STATUS_GROUP_TYPES_TYPE,
  FINANCIAL_RECORD_TYPE,
} from "constants/vendorBills";
import qs from "qs";
import { Pagination } from "types/Models/pagination";
import { BillForwardingEmail, VendorBill } from "types/Models/vendorBills";
import { emptyApi } from "./emptyApi";

export type UpdatePayload = VendorBill & {
  mark_as_active?: boolean;
};

export type PublicPayload = VendorBill & {
  reset_changes?: boolean;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllBills: build.query<
      {
        bills: VendorBill[];
        due_amount: number;
        overdue_amount: number;
        total_amount: number;
        paid_amount: number;
        excel_document_id?: string;
        bills_forwarded_via_email_in_draft_state_count?: number;
        last_invoice_transaction_suggestion_run_at?: string;
        withholding_summary?: {
          total_pending: number;
          due_date: string | null;
          is_overdue: boolean;
        } | null;
      } & Pagination,
      {
        groupId: string;
        entityId: string;
        bill_type: keyof typeof FINANCIAL_RECORD_TYPE;
        page_num?: number;
        search_term: string | null;
        ids?: string;
        merchant_id?: string;
        generate_report?: boolean;
        export_to_email?: boolean;
        minAmount?: string;
        maxAmount?: string;
        startDate?: string;
        endDate?: string;
        dueDateStartDate?: string;
        dueDateEndDate?: string;
        vendorIds?: string;
        statuses?: (keyof typeof BILL_STATUS)[];
        statusGroup?: BILL_STATUS_GROUP_TYPES_TYPE | null;
        sources?: string[];
        customer_uuids?: string;
        limit?: number;
        sortCol?: "INVOICE_DATE";
        sortOrder?: "ASC" | "DSC";
        linkedStatus?: "UNLINKED" | null;
        customerStates?: string[];
      }
    >({
      query: ({
        groupId,
        entityId,
        bill_type,
        search_term,
        page_num,
        ids,
        merchant_id,
        generate_report,
        export_to_email,
        minAmount,
        maxAmount,
        startDate,
        endDate,
        dueDateStartDate,
        dueDateEndDate,
        vendorIds,
        statuses,
        statusGroup,
        sources,
        customer_uuids,
        limit,
        sortCol,
        sortOrder,
        linkedStatus,
        customerStates,
      }) => {
        const query = qs.stringify(
          {
            type: "BILL",
            bill_type: bill_type,
            page_num: page_num,
            search_term: search_term || null,
            ids,
            merchant_id: merchant_id || null,
            generate_report: generate_report || null,
            export_to_email: export_to_email || null,
            min_amount: minAmount || null,
            max_amount: maxAmount || null,
            start_date: startDate || null,
            end_date: endDate || null,
            due_date_start: dueDateStartDate || null,
            due_date_end: dueDateEndDate || null,
            vendor_ids: vendorIds || null,
            statuses: statuses?.length ? statuses : null,
            status_group: statusGroup,
            sources: sources?.length ? sources : null,
            customer_uuids: customer_uuids || null,
            limit: limit || null,
            sort_col: sortCol ?? null,
            sort_order: sortOrder ?? null,
            linked_status: linkedStatus ?? null,
            customer_state: customerStates?.length
              ? customerStates.join(",")
              : null,
          },
          {
            skipNulls: true,
          }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bills/`,
          params: qs.parse(query),
        };
      },
      providesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    getVendorStates: build.query<
      { states: string[] },
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bills/vendor_states/`,
        params: { type: "BILL" },
      }),
      providesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    addBillOrRequest: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        bill_type: keyof typeof FINANCIAL_RECORD_TYPE;
      }
    >({
      query: ({ groupId, entityId, bill_type }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/`,
          method: "POST",
          body: {
            bill_type,
            type: "BILL",
          },
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    uploadBulkBills: build.mutation<
      { bulk_file_id: string },
      {
        entityId: string;
        file: File;
        signal?: AbortSignal;
      }
    >({
      query: ({ entityId, file, signal }) => {
        const formData = new FormData();
        formData.append("excel", file);
        formData.append("type", "BILL");
        return {
          url: `api/inkle/invoicing/entity/${entityId}/bulk_xlsx/`,
          method: "POST",
          body: formData,
          signal,
        };
      },
    }),

    populateBulkBills: build.mutation<
      { bulk_file_id: string },
      {
        entityId: string;
        bulk_file_id: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ entityId, bulk_file_id, signal }) => {
        return {
          url: `api/inkle/invoicing/entity/${entityId}/populate_bulk_xlsx/`,
          method: "POST",
          body: {
            bulk_file_id,
            type: "BILL",
          },
          signal,
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    getBillOrRequest: build.query<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        billId: string;
      }
    >({
      query: ({ groupId, entityId, billId }) => {
        const query = qs.stringify(
          {
            type: "BILL",
          },
          {}
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/`,
          params: qs.parse(query),
        };
      },
      providesTags: (result) =>
        result ? ["VENDOR_BILL", "PUBLIC_VENDOR_BILL"] : [],
    }),

    updateBillOrRequest: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        billId: string;
        payload: Partial<UpdatePayload>;
      }
    >({
      query: ({ groupId, entityId, billId, payload }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/`,
          method: "PATCH",
          body: { ...payload, type: "BILL" },
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    getEmailBillForwarding: build.query<
      BillForwardingEmail,
      {
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/entity_info/`,
          method: "PATCH",
        };
      },
    }),

    uploadBill: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        billId: string;
        bill_document: File;
      }
    >({
      query: ({ groupId, entityId, billId, bill_document }) => {
        const formData = new FormData();
        formData.append("type", "BILL");
        formData.append("bill_document", bill_document);

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    downloadBillOrRequest: build.query<
      { doc_id: string },
      {
        groupId: string;
        entityId: string;
        billId: string;
      }
    >({
      query: ({ groupId, entityId, billId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/download/`,
        };
      },
    }),

    sendRequest: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        billId: string;
        payload: {
          subject: string;
          vendor_bill_public_url: string;
          to: string;
        };
      }
    >({
      query: ({ groupId, entityId, billId, payload }) => {
        const formData = new FormData();
        formData.append("type", "BILL");
        Object.entries(payload).forEach(([key, value]) => {
          formData.append(key, value);
        });

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/send/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    deleteBillOrRequest: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        billId: string;
      }
    >({
      query: ({ groupId, entityId, billId }) => {
        const query = qs.stringify(
          {
            type: "BILL",
          },
          {}
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/vendor_bill/${billId}/`,
          method: "DELETE",
          params: qs.parse(query),
        };
      },
      invalidatesTags: (result) => (result ? ["VENDOR_BILL"] : []),
    }),

    getPubilcBill: build.query<
      VendorBill,
      {
        billId: string;
      }
    >({
      query: ({ billId }) => {
        const query = qs.stringify({
          type: "BILL",
        });
        return {
          url: `/api/inkle/invoicing/vendor_bill/${billId}/`,
          params: qs.parse(query),
        };
      },
      providesTags: (result) =>
        result ? ["PUBLIC_VENDOR_BILL", "VENDOR_BILL"] : [],
    }),

    updatePubilcBill: build.mutation<
      VendorBill,
      { payload: Partial<PublicPayload>; billId: string }
    >({
      query: ({ billId, payload }) => {
        return {
          url: `/api/inkle/invoicing/vendor_bill/${billId}/`,
          method: "PATCH",
          body: { ...payload, type: "BILL" },
        };
      },
      invalidatesTags: (result) => (result ? ["PUBLIC_VENDOR_BILL"] : []),
    }),

    markBillPaid: build.mutation({
      query: ({
        entityId,
        invoiceId,
        transactionIds,
        addedTransactions,
        recordType,
      }: {
        entityId: string;
        invoiceId: string;
        transactionIds?: string[];
        addedTransactions?: {
          date?: string;
          invoice_id?: string | null;
          merchant_data_id?: string;
          transaction_category_id?: string;
          amount: number;
          description: string;
          category_id?: string;
          merchant_id?: string;
        }[];
        recordType: "BILL" | "INVOICE";
      }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/vendor_bill/${invoiceId}/paid/`,
          method: "PUT",
          body: {
            txn_ids: transactionIds,
            split_transactions: addedTransactions,

            type: recordType,
          },
        };
      },
      invalidatesTags: [
        "PUBLIC_VENDOR_BILL",
        "VENDOR_BILL",
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "LINKED_INVOICES",
        "INVOICES",
      ],
    }),

    markBillWithheld: build.mutation<
      VendorBill,
      {
        entityId: string;
        billId: string;
        withheldAmount: number;
        withholdingPaymentDate: string;
      }
    >({
      query: ({
        entityId,
        billId,
        withheldAmount,
        withholdingPaymentDate,
      }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/vendor_bill/${billId}/mark_withheld/`,
        method: "PUT",
        body: {
          withheld_amount: withheldAmount,
          withholding_payment_date: withholdingPaymentDate,
        },
      }),
      invalidatesTags: ["VENDOR_BILL"],
    }),

    extractBillFromImage: build.mutation<
      VendorBill,
      {
        groupId: string;
        entityId: string;
        files: File[];
        billId?: string;
      }
    >({
      query: ({ groupId, entityId, files, billId }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });
        if (billId) {
          formData.append("bill_id", billId);
        }

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/extract_bill/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result ? ["VENDOR_BILL", "VENDORS", "AUTOFILL"] : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllBillsQuery,
  useLazyGetAllBillsQuery,
  useGetVendorStatesQuery,
  useAddBillOrRequestMutation,
  useGetBillOrRequestQuery,
  useLazyGetBillOrRequestQuery,
  useUpdateBillOrRequestMutation,
  useUploadBillMutation,
  useGetEmailBillForwardingQuery,
  useLazyDownloadBillOrRequestQuery,
  useSendRequestMutation,
  useDeleteBillOrRequestMutation,
  useGetPubilcBillQuery,
  useUpdatePubilcBillMutation,
  useUploadBulkBillsMutation,
  usePopulateBulkBillsMutation,
  useMarkBillPaidMutation,
  useMarkBillWithheldMutation,
  useExtractBillFromImageMutation,
} = extendedApi;
